import { FC, Fragment, useState, useEffect, useCallback } from 'react'
import { format as formatDate, isValid, formatDistance } from 'date-fns'
import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import urlJoin from 'url-join'
import {
  AlertCircle,
  AlertTriangle,
  XCircle,
  MoreHorizontal,
  Clock,
} from 'react-feather'
import { FileImageOutlined } from '@ant-design/icons'
import ReactHtmlParser from 'react-html-parser'

import {
  Avatar,
  Button,
  useUtils,
  AssignmentGoogleDocAttachment,
  useAuthState,
  FilePreview,
  getUpdatedDetails,
  PopOver,
  useAssignment,
  AcceptSubmissionModal,
} from '@pepper/ui'
import VERTICAL_IDS from '@pepper/utils/src/constant/enum/vertical_ids'
import { PROJECT_DETAILS_FIELDS } from '@pepper/utils/src/constant/enum/project_fields_label'
import { getFirstLetterFromName } from '@pepper/utils/src/function/misc'
import { AssignmentFieldsLabel } from '@pepper/utils/src/constant/enum/assignment_fileds_label'
import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { updateAssignmentContent } from '@pepper/utils/src/api/assignments/assignments'
import getDocsURL from '@pepper/utils/src/function/getDocsURL'
import checkIsChrome from '@pepper/utils/src/function/checkIsChrome'
import noop from '@pepper/utils/src/function/noop'
import { getAutoApprovalDateText } from 'utils/functions/getAutoApprovalDateText'

import { useSelectedCompanyId } from 'utils/functions/misc'
import FeedbackActivity from 'components/RecentActivity/FeedbackActivity/FeedbackActivity'
import PepperFilePill from './PepperFilePill'
import ProfilePreviewCard from '../ProfilePreview/ProfilePreviewCard'

import { ReactComponent as GoogleDrive } from 'assets/images/google_drive.svg'

import styles from './_.module.css'

interface GoogleDocFile {
  fileUrl: string
}
interface Props {
  projectFormFields: BackendFormField[]
  assignmentBrief: BackendFormField[]
  data: Timeline
  refetch: {
    threads: () => void
    assignmentDetails: () => void
    feedback: () => void
  }
  assignmentId: string
  extraData?: {
    activeStatus: AssignmentStatus
    assignmentTitle: string
    assignmentCategory?: 'text' | 'design' | 'translation' | 'others'
    submissionFiles: Attachment[]
    googleDocFiles: GoogleDocFile[]
    show_pepper_creator?: boolean
    fileSrcType?: string
  }
  latestLogDetails?: {
    state_id: string
    created_at: string
  }
  lastMessageInThread: boolean
  latestAssignmentStatus: boolean
  scrollToBottom: () => void
  projectCategory: ProjectCategory
  reworkIndex: number
  rowIndex: number
  submissionText?: string
}

// Logs
const Log: FC<Props> = ({
  data,
  refetch,
  extraData,
  latestLogDetails,
  assignmentId,
  lastMessageInThread,
  scrollToBottom,
  latestAssignmentStatus,
  projectCategory,
  projectFormFields,
  assignmentBrief,
  reworkIndex,
  rowIndex,
  submissionText = '',
}) => {
  const { feedback_inputs } = useUtils()
  const { data: assignment } = useAssignment({ assignmentId })
  const { user } = useAuthState()
  const companyId = useSelectedCompanyId()
  const acceptAssignmentModal = useModal(AcceptSubmissionModal)
  const declineAssignmentModal = useModal('decline-assignment-submission')
  const reworkConfirmationModal = useModal('rework-confirmation')

  const [shouldShowReworkRejectMenu, setShouldShowReworkRejectMenu] = useState<
    'open' | 'close'
  >('close')

  useEffect(() => {
    lastMessageInThread && scrollToBottom()
  }, [lastMessageInThread, scrollToBottom])

  const openAcceptAssignmentModal = useCallback(
    isApprovalNeeded => {
      acceptAssignmentModal.show({
        assignmentIds: [assignmentId],
        shouldSubmitAssignmentApproval: isApprovalNeeded,
        companyId,
        refetch: () => {
          refetch?.feedback()
          refetch?.assignmentDetails()
          refetch?.threads()
        },
      })
    },
    [acceptAssignmentModal, refetch, companyId]
  )

  const autoApprovalText = getAutoApprovalDateText(
    data.client_approval_deadline
  )

  const getLogComponent = () => {
    const isChrome = checkIsChrome()

    /** render this component if assignment status is CLI_RWK or CLI_RJT (Rework or Reject) */
    if (
      [ASSIGNMENT_STATUS.CLI_RWK.id, ASSIGNMENT_STATUS.CLI_RJT.id].includes(
        data.status_code
      )
    ) {
      /** Set Issues */
      const issues: string[] = []
      data.rework_areas_data.forEach(reworkData => {
        reworkData.id !== 25 &&
          reworkData.inputs.forEach(inputId => {
            const inputTitle = feedback_inputs.find(
              input => input.id === inputId
            )?.title
            if (inputTitle) {
              issues.push(inputTitle)
            }
          })
      })
      return (
        <div className={styles.declinedFeedbackContainer}>
          <FeedbackActivity
            title={
              ASSIGNMENT_STATUS.CLI_RWK.id === data?.status_code
                ? 'Rework Requested'
                : 'Assignment Rejected'
            }
            titlePostfix={
              ASSIGNMENT_STATUS.CLI_RWK.id === data?.status_code ? (
                <AlertCircle size={14} />
              ) : (
                <XCircle size={14} />
              )
            }
            issues={issues}
            message={data?.reason ?? ''}
            attachments={data?.rework_attachments ?? []}
            issuesPrefix={<AlertTriangle size={14} />}
            rating={0}
            type={
              ASSIGNMENT_STATUS.CLI_RWK.id === data?.status_code
                ? 'warn'
                : 'danger'
            }
          />
        </div>
      )
    } else {
      return (
        <Fragment>
          {data.isLatest && (
            <Fragment>
              {assignment?.vertical.id !==
                VERTICAL_IDS.SHORT_FORM_TRANSLATION && (
                <div className={styles.fileList} id={data?.state_id}>
                  {typeof extraData?.assignmentCategory === 'string' &&
                  ['design', 'others'].includes(
                    extraData?.assignmentCategory
                  ) ? (
                    <Fragment>
                      {extraData?.submissionFiles.map(
                        (file: Attachment, index: number) => (
                          <div className={styles.filePreview} key={index}>
                            <FilePreview
                              file={file}
                              fallbackImage={<FileImageOutlined />}
                            />
                          </div>
                        )
                      )}
                      {extraData?.assignmentCategory === 'others' &&
                        submissionText && (
                          <div className={styles.submissionText}>
                            <span>Submission Text:</span>{' '}
                            {ReactHtmlParser(submissionText)}
                          </div>
                        )}
                    </Fragment>
                  ) : (
                    extraData?.googleDocFiles.map(
                      (file: GoogleDocFile, index: number) => (
                        <div key={index}>
                          {extraData?.fileSrcType === 'pepper_editor' ? (
                            <PepperFilePill
                              assignmentId={assignmentId}
                              assignmentName={assignment?.assignment_title}
                              assignmentCode={assignment?.assignment_code}
                              link={`/${companyId}/document/${assignmentId}`}
                              name={extraData?.assignmentTitle ?? ''}
                            />
                          ) : (
                            <AssignmentGoogleDocAttachment
                              assignmentId={assignmentId}
                              fallbackImage={<GoogleDrive />}
                              assignmentTitle={extraData?.assignmentTitle ?? ''}
                              fileUrl={
                                isChrome && assignment?.show_pepper_docs
                                  ? urlJoin(
                                      `/${companyId}/`,
                                      getDocsURL(assignmentId)
                                    )
                                  : file.fileUrl
                              }
                              downloadFormat="docx"
                              downloadable={true}
                            />
                          )}
                        </div>
                      )
                    )
                  )}
                </div>
              )}
              {assignment?.vertical.id ===
                VERTICAL_IDS.SHORT_FORM_TRANSLATION && (
                <div className={clsx(styles.submissionText, styles.mt2)}>
                  <span>Submission Text:</span>{' '}
                  {ReactHtmlParser(submissionText)}
                </div>
              )}
            </Fragment>
          )}

          {!data.isLatest &&
            !data.is_resolved &&
            !data.is_unscrapped &&
            !(
              extraData?.show_pepper_creator &&
              [ASSIGNMENT_STATUS.WRI_WRK.id].includes(data.status_code)
            ) &&
            !data.changes?.length &&
            ![
              ASSIGNMENT_STATUS.HOLD.id,
              ASSIGNMENT_STATUS.SCRAPPED.id,
            ].includes(data.status_code) && (
              <p className={styles.reSubmittedText}>
                Assignment has been re-submitted{' '}
                {latestLogDetails?.created_at
                  ? formatDistance(
                      new Date(latestLogDetails?.created_at),
                      new Date(),
                      { addSuffix: true }
                    )
                  : ''}
                <a
                  href={`#${latestLogDetails?.state_id}`}
                  className={styles.viewLatestSubmissionLink}
                >
                  View latest submission
                </a>
              </p>
            )}

          {data.status_code === ASSIGNMENT_STATUS.CLI_AWA.id &&
          data.isLatest &&
          extraData?.activeStatus === ASSIGNMENT_STATUS.CLI_AWA.id ? (
            <>
              {data.client_approval_deadline && !!autoApprovalText && (
                <div className={clsx(styles.cell, styles.textHighlight)}>
                  <span>
                    <Clock size={16} />
                    {autoApprovalText}
                  </span>
                </div>
              )}
              <div className={styles.assignmentsActionBtn}>
                <Button
                  type="primary"
                  className={styles.approve}
                  onClick={() => {
                    openAcceptAssignmentModal(true)
                    updateAssignmentContent(data.assignment_id)
                  }}
                >
                  Approve Assignment
                </Button>
                <Button
                  type="primary"
                  className={styles.rework}
                  ghost={true}
                  onClick={() => {
                    declineAssignmentModal.show({
                      assignment: { id: data.assignment_id },
                      handleOnOkClick: refetch.assignmentDetails,
                      handleOnCancelClick: noop,
                      type: 'rework',
                      projectCategory,
                      defaultComment: '',
                      assignmentData: data,
                    })
                    updateAssignmentContent(data.assignment_id)
                  }}
                >
                  Request Rework
                </Button>

                <PopOver
                  content={
                    <Button
                      type="text"
                      className={styles.reject}
                      onClick={() => {
                        reworkConfirmationModal.show({
                          assignmentId: data.assignment_id,
                          projectCategory,
                          refetch,
                        })
                        setShouldShowReworkRejectMenu(
                          shouldShowReworkRejectMenu === 'open'
                            ? 'open'
                            : 'close'
                        )
                      }}
                    >
                      Unsatisfied with the content
                    </Button>
                  }
                  visible={shouldShowReworkRejectMenu === 'open'}
                  trigger="click"
                  placement="bottomLeft"
                  overlayClassName={styles.popOver}
                >
                  <Button
                    type="default"
                    className={styles.filterBtn}
                    onClick={() => {
                      setShouldShowReworkRejectMenu(
                        shouldShowReworkRejectMenu === 'close'
                          ? 'open'
                          : 'close'
                      )
                    }}
                  >
                    <MoreHorizontal size={16} />
                  </Button>
                </PopOver>
              </div>
            </>
          ) : (
            ''
          )}
        </Fragment>
      )
    }
  }

  const getHelpText = (assignerName: string) => {
    if (data.is_resolved) {
      return 'Assignment has been resolved'
    }

    if (data.is_unscrapped) {
      return 'Assignment has been un-scrapped'
    }

    const assignerNameInLowerCase = assignerName?.toLowerCase()
    const userNameInLowerCase = user?.full_name?.toLowerCase()

    if (
      Array.isArray(data.changes) &&
      data.changes.length > 0 &&
      assignerNameInLowerCase !== userNameInLowerCase
    ) {
      return (
        <span>
          Updated
          {data.changes.map((change, index) => {
            return (
              <Fragment key={index}>
                <PopOver
                  placement="bottom"
                  content={() => (
                    <div className={styles.displayDetails}>
                      {data.event_type === 'assignment_details_updated' &&
                        getUpdatedDetails(
                          assignmentBrief,
                          AssignmentFieldsLabel[change.key]?.type,
                          AssignmentFieldsLabel[change.key]?.title,
                          change
                        )}
                      {data.event_type === 'project_details_updated' &&
                        getUpdatedDetails(
                          projectFormFields,
                          PROJECT_DETAILS_FIELDS[change.key]?.type,
                          PROJECT_DETAILS_FIELDS[change.key]?.title,
                          change
                        )}
                    </div>
                  )}
                >
                  <span className={styles.assignmentFieldLabel}>
                    {data.event_type === 'assignment_details_updated' &&
                      AssignmentFieldsLabel[change.key]?.title}
                    {data.event_type === 'project_details_updated' &&
                      PROJECT_DETAILS_FIELDS[change.key]?.title}
                  </span>
                </PopOver>
                {Array.isArray(data.changes) &&
                  data.changes.length > 1 &&
                  index !== data.changes.length - 1 &&
                  (data.changes.length - 2 === index ? ', and' : ',')}
              </Fragment>
            )
          })}
        </span>
      )
    }

    switch (data?.status_code) {
      case ASSIGNMENT_STATUS.CLI_AWA.id: {
        return 'Assignment submitted for review'
      }

      case ASSIGNMENT_STATUS.CLI_RWK.id: {
        if (assignerName !== user?.full_name) {
          // Client ops marking rework on client's behalf
          return 'Marked this assignment as rework on your behalf'
        }

        return 'Reviewed the assignment and requested for a rework'
      }

      case ASSIGNMENT_STATUS.CLI_FIN.id: {
        if (data?.auto_approved_days && data?.client_approval_deadline) {
          return `This assignment was approved automatically as it stayed in the submitted status for over ${
            data?.auto_approved_days ?? ''
          } days.`
        }
        if (assignerName !== user?.full_name) {
          // Client ops marking approved on client's behalf
          if (!assignment)
            return 'Reviewed and approved the assignment on your behalf'
          return assignment?.client_feedback?.length > 0
            ? 'Reviewed and approved the assignment on your behalf'
            : 'Approved the assignment on your behalf'
        }

        if (!assignment) return 'Reviewed and approved the assignment'
        return assignment?.client_feedback?.length > 0
          ? 'Reviewed and approved the assignment'
          : 'Approved the assignment'
      }

      case ASSIGNMENT_STATUS.HOLD.id: {
        return 'Has put assignment on hold'
      }

      case ASSIGNMENT_STATUS.SCRAPPED.id: {
        return 'Has scrapped the assignment'
      }

      case ASSIGNMENT_STATUS.WRI_WRK.id: {
        const DeadlineDate = assignment?.client_deadline

        const formattedDeadline =
          typeof DeadlineDate === 'string' &&
          isValid(new Date(DeadlineDate)) &&
          formatDate(new Date(DeadlineDate), 'do LLLL, yyyy')

        const isRework =
          reworkIndex > -1 && rowIndex > -1 && reworkIndex < rowIndex

        const assignee = data?.assignee_details

        const assigneeName =
          assignee?.display_name?.trim() ||
          `${assignee?.first_name} ${assignee?.last_name?.charAt(0)}`

        const workAndReworktext = () => {
          return (
            <div>
              <span className={styles.assigneeName}>{assigneeName} </span>
              {`has started ${
                isRework ? 're-working ' : 'working'
              } on this item with a deadline of ${formattedDeadline}.`}
            </div>
          )
        }

        const passAndRejectText = () => {
          return (
            <>
              <span className={styles.assigneeName}>{assigneeName} </span>
              {`is assigned to ${
                isRework ? ' rework ' : ' work '
              } for this assignment`}
              {assignment?.client_deadline &&
                ` Assignment deadline has been set to ${formattedDeadline}.`}
              {assignee?.show_previous_assignee_rejected &&
                'as the previous one has either passed/been rejected'}
            </>
          )
        }
        return (
          <>
            {extraData?.show_pepper_creator &&
              assignee?.show_latest_assignee && (
                <>
                  {assignee?.show_previous_assignee_rejected
                    ? passAndRejectText()
                    : workAndReworktext()}
                  {/* {assignee?.show_latest_assignee &&
                  `. Here’s the profile of the creator`} */}
                  {assignee?.show_latest_assignee && (
                    <ProfilePreviewCard
                      data={assignee}
                      refetch={refetch.assignmentDetails}
                    />
                  )}
                </>
              )}
          </>
        )
      }

      default: {
        return 'Reviewed and rejected the assignments'
      }
    }
  }

  const getUserDetailsByAssignmentStatus = () => {
    return data?.status_code === ASSIGNMENT_STATUS.CLI_RJT.id
      ? data?.assignee_details
      : data?.assigner_details
  }

  const getName = () => {
    const userData = getUserDetailsByAssignmentStatus()
    const firstName = userData?.first_name || ''
    const lastName = userData?.last_name || ''
    return getFirstLetterFromName(firstName, lastName)
  }

  if (
    data?.status_code === ASSIGNMENT_STATUS.WRI_WRK.id &&
    extraData?.show_pepper_creator &&
    !data?.assignee_details?.show_latest_assignee
  ) {
    return null
  }

  const userName = getName()

  return (
    <div
      className={clsx(styles.threadContainer, {
        [styles.highlightLastThread]: latestAssignmentStatus,
      })}
    >
      <div className={styles.profilePic}>
        <Avatar
          nameChar={userName}
          className={styles.avatar}
          src={data.user_avatar ?? getUserDetailsByAssignmentStatus()?.avatar}
        >
          {userName}
        </Avatar>
      </div>
      <div className={styles.threadDetails}>
        <div className={styles.userDetails}>
          <span className={styles.name}>
            {data.updated_by ?? getUserDetailsByAssignmentStatus()?.full_name}
          </span>

          <span className={styles.threadTimestamp}>
            {formatDistance(new Date(data.created_at), new Date(), {
              addSuffix: true,
            })}
          </span>
        </div>
        <div className={styles.reviewFileContainer}>
          <p className={styles.reviewHelpText}>
            {getHelpText(getUserDetailsByAssignmentStatus()?.full_name ?? '')}
          </p>
          <div className={styles.logComponent}>{getLogComponent()}</div>
        </div>
      </div>
    </div>
  )
}
export default Log
